// Дерево категорий витрины (дерево v2, миграция 134): до трёх уровней —
// аудитория → тип → вид («Женщинам / Обувь / Кроссовки и кеды»).
//
// Витрина больше не держит своих списков категорий и id корней: разделы
// шапки, колонки мега-меню, корни ленты и фильтр каталога строятся из
// GET /catalog/categories (и дерева фасетов). Переименовал или добавил узел в
// консоли — витрина поехала за ним.
import { ARCHIVED_V1_CATEGORY_IDS, V1_FALLBACK } from "@osmix/catalog-core/category-tree";

import type { ArchivedCategory, Category, CategoryFacet, FeedSegment } from "@/api";

/** Раздел шапки: «Одежда» (корни-аудитории) или отдельный корень («Красота», «Дом»). */
export type DepartmentOption = {
	/** `clothes` — аудитории; для остальных — раздел корня (`home`, `beauty`) или его id. */
	key: string;
	label: string;
	/** Корни дерева, из которых состоит раздел. */
	rootIds: string[];
};

export const CLOTHES_DEPARTMENT = "clothes";

const GENDER_SEGMENTS: { gender: string; segment: FeedSegment }[] = [
	{ gender: "female", segment: "womens" },
	{ gender: "male", segment: "mens" },
	{ gender: "kids", segment: "kids" },
];

const isAudienceRoot = (root: Category) => GENDER_SEGMENTS.some((g) => g.gender === root.gender);

/**
 * Разделы шапки по корням дерева: сначала «Одежда» (все корни с полом
 * «женский / мужской / детский»), затем каждый прочий корень своим пунктом —
 * в порядке дерева. Ключ прочего раздела — его раздел витрины (`home`,
 * `beauty`), чтобы выбор переживал перезагрузку и переименование корня.
 */
export function departmentsFromTree(categories: Category[]): DepartmentOption[] {
	const audience = categories.filter(isAudienceRoot);
	const out: DepartmentOption[] = [];
	if (audience.length) {
		out.push({ key: CLOTHES_DEPARTMENT, label: "Одежда", rootIds: audience.map((c) => c.id) });
	}
	for (const root of categories) {
		if (isAudienceRoot(root)) continue;
		const byKey = root.section && !out.some((d) => d.key === root.section) ? root.section : root.id;
		out.push({ key: byKey, label: root.name, rootIds: [root.id] });
	}
	return out;
}

/** Корни раздела; неизвестный раздел (устаревший ключ в настройках) — «Одежда». */
export function departmentRootIds(categories: Category[], department: string): string[] {
	const departments = departmentsFromTree(categories);
	return (
		departments.find((d) => d.key === department)?.rootIds ??
		departments.find((d) => d.key === CLOTHES_DEPARTMENT)?.rootIds ??
		[]
	);
}

/** Корни, которые показывает раздел: сами узлы дерева, в порядке дерева. */
export function departmentRoots(categories: Category[], department: string): Category[] {
	const ids = new Set(departmentRootIds(categories, department));
	return categories.filter((c) => ids.has(c.id));
}

/** Корень аудитории для таба «Женщинам / Мужчинам / Детям». */
export function segmentRootIds(categories: Category[]): Partial<Record<FeedSegment, string>> {
	const out: Partial<Record<FeedSegment, string>> = {};
	for (const { gender, segment } of GENDER_SEGMENTS) {
		const root = categories.find((c) => c.gender === gender);
		if (root) out[segment] = root.id;
	}
	return out;
}

/**
 * Колонка мега-меню: корень и его группы второго уровня со своими листьями.
 * У корня без третьего уровня («Детям», «Красота») группы — это сами листья
 * (`leaves` пустые), у «Женщинам» — «Одежда / Обувь / Аксессуары» с видами.
 */
export type MegaColumn = {
	root: Category;
	groups: { node: Category; leaves: Category[] }[];
};

export function megaColumns(categories: Category[], department: string): MegaColumn[] {
	return departmentRoots(categories, department).map((root) => ({
		root,
		groups: (root.children ?? []).map((node) => ({ node, leaves: node.children ?? [] })),
	}));
}

/** Путь до узла в дереве фасетов (от корня), пусто — узла нет. */
export function facetPath(nodes: CategoryFacet[], id: string): CategoryFacet[] {
	for (const node of nodes) {
		if (node.value === id) return [node];
		const tail = facetPath(node.children ?? [], id);
		if (tail.length) return [node, ...tail];
	}
	return [];
}

export type CategoryTreeRow = {
	node: CategoryFacet;
	depth: number;
	hasChildren: boolean;
	open: boolean;
};

/**
 * Видимые строки фильтра категорий: корни всегда, дети — только у раскрытых
 * узлов. Путь к выбранной категории раскрыт всегда, чтобы выбор было видно.
 */
export function visibleTreeRows(
	nodes: CategoryFacet[],
	expanded: ReadonlySet<string>,
	selectedId?: string | null,
): CategoryTreeRow[] {
	const forced = new Set(selectedId ? facetPath(nodes, selectedId).map((n) => n.value) : []);
	const out: CategoryTreeRow[] = [];
	const visit = (list: CategoryFacet[], depth: number) => {
		for (const node of list) {
			const hasChildren = Boolean(node.children?.length);
			const open = hasChildren && (expanded.has(node.value) || (forced.has(node.value) && node.value !== selectedId));
			out.push({ node, depth, hasChildren, open });
			if (open) visit(node.children ?? [], depth + 1);
		}
	};
	visit(nodes, 0);
	return out;
}

/** Найти узел дерева категорий по id. */
export function findCategory(categories: Category[], id: string): Category | undefined {
	for (const c of categories) {
		if (c.id === id) return c;
		const found = findCategory(c.children ?? [], id);
		if (found) return found;
	}
	return undefined;
}

/** Путь до узла дерева категорий API (от корня); пусто — узла нет (архив, опечатка). */
export function categoryPath(categories: Category[], id: string): Category[] {
	for (const node of categories) {
		if (node.id === id) return [node];
		const tail = categoryPath(node.children ?? [], id);
		if (tail.length) return [node, ...tail];
	}
	return [];
}

const SEGMENT_AUDIENCE: Record<FeedSegment, "female" | "male" | "kids"> = {
	womens: "female",
	mens: "male",
	kids: "kids",
};

// ЗАПАСНОЙ путь, когда ручка архивных узлов недоступна или ещё не ответила:
// узлы v1 без смыслового наследника (электроника, питомцы) ведут в корень.
const NO_SUCCESSOR = new Set<string>(["cat-9", "cat-10"]);

/**
 * Куда вести ссылку на категорию, которой нет в дереве API.
 *
 * - узел есть в дереве — он сам;
 * - архивный узел, о котором знает шлюз (`GET /catalog/categories/archived`,
 *   `archived`), — его преемник ИЗ API: под аудиторию покупателя (таб
 *   «Женщинам / Мужчинам / Детям»), иначе преемник по умолчанию; `null` у
 *   шлюза — преемника нет, корень каталога;
 * - ручка не ответила (`archived` не передан) или не знает узла — запасная
 *   таблица переноса товаров (`@osmix/catalog-core`): `cat-3` «Обувь», `cat-8`
 *   «Аксессуары», `cat-6` «Здоровье» — преемник, электроника и питомцы — корень;
 * - неизвестный id — `null`: корень каталога.
 *
 * Преемник, которого нет в дереве (узел архивировали в консоли), тоже даёт корень.
 */
export function resolveCategoryId(
	categories: Category[],
	id: string,
	segment: FeedSegment = "womens",
	archived?: readonly ArchivedCategory[],
): string | null {
	if (categoryPath(categories, id).length) return id;
	const inTree = (successor: string | null | undefined): string | null =>
		successor && categoryPath(categories, successor).length ? successor : null;
	const fromApi = archived?.find((node) => node.id === id);
	if (fromApi) return inTree(fromApi.successors?.[SEGMENT_AUDIENCE[segment]] ?? fromApi.successorId);
	const isArchivedV1 = (ARCHIVED_V1_CATEGORY_IDS as readonly string[]).includes(id);
	if (!isArchivedV1 || NO_SUCCESSOR.has(id)) return null;
	return inTree(V1_FALLBACK[id]?.[SEGMENT_AUDIENCE[segment]]);
}

/**
 * Подкатегории страницы категории — прямые дети узла в дереве API. С деревом
 * фасетов (счётчики выдачи) пустые ветки скрыты, чтобы не вести в пустоту.
 */
export function subcategoriesOf(
	categories: Category[],
	id: string,
	facets?: CategoryFacet[],
): { id: string; name: string; count?: number }[] {
	const node = categoryPath(categories, id).at(-1);
	const children = node?.children ?? [];
	if (!facets) return children.map((c) => ({ id: c.id, name: c.name }));
	const facetChildren = facetPath(facets, id).at(-1)?.children ?? [];
	const counts = new Map(facetChildren.map((f) => [f.value, f.count]));
	return children
		.filter((c) => (counts.get(c.id) ?? 0) > 0)
		.map((c) => ({ id: c.id, name: c.name, count: counts.get(c.id) }));
}
